import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI, isAuthFailure } from '../services/api';

const AuthContext = createContext(null);
const IDLE_LOGOUT_MS = 4 * 60 * 60 * 1000;   // auto-logout after 4h of inactivity
const IDLE_WARN_BEFORE_MS = 5 * 60 * 1000;   // warn 5 minutes before that
const ACTIVITY_KEY = 'mc_last_activity';     // shared across tabs

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idleWarning, setIdleWarning] = useState(false);
  const idleTimer = useRef(null);
  const lastWrite = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem('oms_token');
    if (token) {
      authAPI.me()
        .then(res => setUser(res.data.user))
        .catch(err => {
          // Delete the session ONLY when the server says the token is bad. A network drop, a 500 or
          // a cold start used to land here too — one flaky page load and the user was signed out.
          if (isAuthFailure(err)) { localStorage.removeItem('oms_token'); localStorage.removeItem('oms_dl_token'); }
        })
        .finally(() => setLoading(false));
      // Self-heal (A48): sessions that logged in before the scope-restricted download token existed
      // never received one. Mint one now so label/invoice/manifest links keep working without
      // forcing a fresh login. Fire-and-forget — a failure here just means those links 401 until
      // the next successful call happens to retry it.
      if (!localStorage.getItem('oms_dl_token')) {
        authAPI.getDownloadToken()
          .then(res => localStorage.setItem('oms_dl_token', res.data.dl_token))
          .catch(() => {});
      }
    } else {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('oms_token');
    localStorage.removeItem('oms_dl_token');
    localStorage.removeItem(ACTIVITY_KEY);
    setIdleWarning(false);
    setUser(null);
  }, []);

  // Auto-logout after 4h of inactivity. Two things the old timer got wrong:
  //  - it evicted you silently, mid-task, with no chance to stay
  //  - each browser tab kept its own timer, so working in one tab didn't keep another alive
  // Activity is now written to localStorage, which every tab reads, and a warning appears first.
  useEffect(() => {
    if (!user) return undefined;

    const markActive = () => {
      const now = Date.now();
      // Throttle: one write every 10s is plenty and keeps this off the hot path of every scroll.
      if (now - lastWrite.current < 10_000) return;
      lastWrite.current = now;
      localStorage.setItem(ACTIVITY_KEY, String(now));
      setIdleWarning(false);
    };

    const lastActivity = () => Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now();

    const tick = () => {
      const idleFor = Date.now() - lastActivity();
      if (idleFor >= IDLE_LOGOUT_MS) { logout(); return; }
      setIdleWarning(idleFor >= IDLE_LOGOUT_MS - IDLE_WARN_BEFORE_MS);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, markActive, { passive: true }));
    // Another tab reporting activity keeps this one signed in too.
    const onStorage = (e) => { if (e.key === ACTIVITY_KEY) setIdleWarning(false); };
    window.addEventListener('storage', onStorage);

    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    idleTimer.current = setInterval(tick, 30_000);

    return () => {
      events.forEach(e => window.removeEventListener(e, markActive));
      window.removeEventListener('storage', onStorage);
      clearInterval(idleTimer.current);
    };
  }, [user, logout]);

  // "Stay signed in" — reset the shared activity stamp and dismiss the warning.
  const staySignedIn = useCallback(() => {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    lastWrite.current = Date.now();
    setIdleWarning(false);
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    localStorage.setItem('oms_token', res.data.token);
    if (res.data.dl_token) localStorage.setItem('oms_dl_token', res.data.dl_token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, mobile, password) => {
    const res = await authAPI.registerInit({ name, email, mobile, password });
    return res.data;
  };

  // Verify signup OTP → account created + auto-logged-in (token returned) → onboarding next.
  const verifyRegister = async (email, otp) => {
    const res = await authAPI.registerVerify({ email, otp });
    if (res.data.token) {
      localStorage.setItem('oms_token', res.data.token);
      if (res.data.dl_token) localStorage.setItem('oms_dl_token', res.data.dl_token);
      setUser(res.data.user);
    }
    return res.data;
  };

  const refreshUser = useCallback(async () => {
    try { const res = await authAPI.me(); setUser(res.data.user); return res.data.user; } catch { return null; }
  }, []);

  const dismissWelcome = useCallback(async () => {
    setUser(prev => prev ? { ...prev, welcome_pending: false } : prev);
    try { await authAPI.welcomeSeen(); } catch { /* non-fatal */ }
  }, []);

  const completeOnboarding = async (data) => {
    const res = await authAPI.onboarding(data);
    setUser(res.data.user);
    return res.data;
  };

  const updateProfile = async (data) => {
    const res = await authAPI.updateProfile(data);
    setUser(res.data.user);
    return res.data;
  };

  const forceChangePassword = async (new_password, confirm_password) => {
    const res = await authAPI.forceChangePassword({ new_password, confirm_password });
    // A password change signs out every other device; this one is handed a fresh token.
    if (res.data?.token) localStorage.setItem('oms_token', res.data.token);
    setUser(prev => ({ ...prev, force_password_change: false }));
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, verifyRegister, refreshUser, dismissWelcome, completeOnboarding, updateProfile, forceChangePassword, logout, idleWarning, staySignedIn }}>
      {children}
      {user && idleWarning && (
        <div style={idleStyles.overlay}>
          <div style={idleStyles.card}>
            <div style={idleStyles.title}>Still there?</div>
            <div style={idleStyles.body}>
              You’ve been inactive for a while. For your security we’ll sign you out in about 5 minutes.
            </div>
            <div style={idleStyles.actions}>
              <button style={idleStyles.secondary} onClick={logout}>Sign out</button>
              <button style={idleStyles.primary} onClick={staySignedIn}>Stay signed in</button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

const idleStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 },
  card: { background: '#fff', borderRadius: 14, padding: 24, width: 380, maxWidth: '90vw', boxShadow: 'var(--mc-e4)' },
  title: { fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
  body: { fontSize: 13.5, color: '#475569', lineHeight: 1.6, marginBottom: 20 },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  secondary: { background: '#fff', border: '1px solid #e2e8f0', color: '#475569', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' },
  primary: { background: '#4f46e5', border: 'none', color: '#fff', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export const useAuth = () => useContext(AuthContext);
